import { type CRRFlashQuestion } from './crr-flash-questions';

export type CotierFlashQuestion = Omit<CRRFlashQuestion, 'category'> & {
  category: 'navigation' | 'balisage' | 'meteo' | 'securite' | 'reglementation';
};

export const cotierFlashQuestions: CotierFlashQuestion[] = [
  // ── Navigation ──
  {
    id: "cot-noeud",
    question_fr: "Qu'est-ce qu'un nœud ?",
    question_en: "What is a knot?",
    answer_fr: "1 nœud = 1 mille nautique/heure = 1,852 km/h",
    answer_en: "1 knot = 1 nautical mile/hour = 1.852 km/h",
    context_fr: "Unité de vitesse standard en navigation maritime.",
    context_en: "Standard speed unit in maritime navigation.",
    category: "navigation"
  },
  {
    id: "cot-mille",
    question_fr: "Combien mesure un mille nautique ?",
    question_en: "How long is a nautical mile?",
    answer_fr: "1 852 mètres",
    answer_en: "1,852 meters",
    context_fr: "Correspond à 1 minute d'arc de latitude.",
    context_en: "Equals 1 minute of arc of latitude.",
    category: "navigation"
  },
  {
    id: "cot-relev",
    question_fr: "Qu'est-ce qu'un relèvement ?",
    question_en: "What is a bearing?",
    answer_fr: "L'angle entre le Nord et la direction d'un objet observé",
    answer_en: "The angle between North and the direction of an observed object",
    context_fr: "Permet de se situer par rapport à un amer ou un phare.",
    context_en: "Helps locate yourself relative to a landmark or lighthouse.",
    category: "navigation"
  },
  {
    id: "cot-cap",
    question_fr: "Quelle est la différence entre cap et route ?",
    question_en: "What is the difference between heading and course?",
    answer_fr: "Le cap est la direction du bateau, la route est la trajectoire réelle sur l'eau",
    answer_en: "Heading is the boat's direction, course is the actual path on water",
    context_fr: "Le courant et le vent créent la dérive entre cap et route.",
    context_en: "Current and wind create drift between heading and course.",
    category: "navigation"
  },
  // ── Balisage ──
  {
    id: "cot-laterale",
    question_fr: "Que signifie une bouée rouge (latérale bâbord) ?",
    question_en: "What does a red buoy (port lateral) mean?",
    answer_fr: "Elle marque le côté bâbord (gauche) du chenal en venant du large",
    answer_en: "It marks the port (left) side of the channel when coming from sea",
    context_fr: "En IALA zone A (Europe), rouge = bâbord en entrant au port.",
    context_en: "In IALA zone A (Europe), red = port when entering harbor.",
    category: "balisage"
  },
  {
    id: "cot-cardinal",
    question_fr: "Que signifie une marque cardinale Nord ?",
    question_en: "What does a North cardinal mark mean?",
    answer_fr: "Passez au NORD de la marque pour éviter le danger",
    answer_en: "Pass to the NORTH of the mark to avoid danger",
    context_fr: "Deux cônes pointe en haut = Nord. Couleur noire et jaune.",
    context_en: "Two cones point up = North. Black and yellow color.",
    category: "balisage"
  },
  {
    id: "cot-danger-isole",
    question_fr: "Comment reconnaître une marque de danger isolé ?",
    question_en: "How to identify an isolated danger mark?",
    answer_fr: "Noire avec bande(s) rouge(s) horizontale(s), 2 sphères noires au sommet",
    answer_en: "Black with red horizontal band(s), 2 black spheres on top",
    context_fr: "Le danger est sous la bouée, on peut passer autour.",
    context_en: "The danger is under the buoy, you can pass around it.",
    category: "balisage"
  },
  // ── Météo ──
  {
    id: "cot-beaufort",
    question_fr: "Que représente l'échelle de Beaufort ?",
    question_en: "What does the Beaufort scale represent?",
    answer_fr: "La force du vent de 0 (calme) à 12 (ouragan)",
    answer_en: "Wind force from 0 (calm) to 12 (hurricane)",
    context_fr: "Force 7 = coup de vent, navigation déconseillée pour les plaisanciers.",
    context_en: "Force 7 = gale, navigation not recommended for leisure craft.",
    category: "meteo"
  },
  {
    id: "cot-bms",
    question_fr: "Que signifie BMS ?",
    question_en: "What does BMS stand for?",
    answer_fr: "Bulletin Météo Spécial — alerte vent ≥ force 7",
    answer_en: "Special Weather Bulletin — wind alert ≥ force 7",
    context_fr: "Diffusé par les CROSS sur VHF canal 16 puis canal de travail.",
    context_en: "Broadcast by CROSS on VHF channel 16 then working channel.",
    category: "meteo"
  },
  // ── Sécurité ──
  {
    id: "cot-gilet",
    question_fr: "Quand le gilet de sauvetage est-il obligatoire ?",
    question_en: "When is a life jacket mandatory?",
    answer_fr: "1 gilet par personne à bord, obligatoire au-delà de 300m du rivage",
    answer_en: "1 life jacket per person on board, mandatory beyond 300m from shore",
    context_fr: "En pratique, portez-le toujours. 150N minimum pour la haute mer.",
    context_en: "In practice, always wear one. 150N minimum for open sea.",
    category: "securite"
  },
  {
    id: "cot-fusees",
    question_fr: "Combien de fusées de détresse faut-il au-delà de 6 milles ?",
    question_en: "How many distress flares beyond 6 miles?",
    answer_fr: "3 fusées à parachute + 2 fumigènes flottants",
    answer_en: "3 parachute rockets + 2 floating smoke signals",
    context_fr: "Vérifiez la date de péremption régulièrement.",
    context_en: "Check the expiration date regularly.",
    category: "securite"
  },
  {
    id: "cot-extincteur",
    question_fr: "Quand l'extincteur est-il obligatoire à bord ?",
    question_en: "When is a fire extinguisher mandatory on board?",
    answer_fr: "Obligatoire dès qu'il y a un moteur à bord (thermique)",
    answer_en: "Mandatory as soon as there is an engine on board (combustion)",
    context_fr: "Type ABC recommandé, accessible rapidement.",
    context_en: "Type ABC recommended, quickly accessible.",
    category: "securite"
  },
  // ── Réglementation ──
  {
    id: "cot-priorite",
    question_fr: "Qui est prioritaire entre un voilier et un chalutier ?",
    question_en: "Who has priority between a sailboat and a trawler?",
    answer_fr: "Le chalutier (navire à capacité de manœuvre restreinte)",
    answer_en: "The trawler (vessel restricted in ability to maneuver)",
    context_fr: "Hiérarchie : non-maître de sa manœuvre > capacité restreinte > voilier > moteur.",
    context_en: "Hierarchy: not under command > restricted > sail > power.",
    category: "reglementation"
  },
  {
    id: "cot-zone-300m",
    question_fr: "Quelle est la zone des 300 mètres ?",
    question_en: "What is the 300-meter zone?",
    answer_fr: "Zone interdite aux engins à moteur (sauf accès perpendiculaire à la côte)",
    answer_en: "Zone prohibited for motorized craft (except perpendicular shore access)",
    context_fr: "Protège les baigneurs et les activités de plage.",
    context_en: "Protects swimmers and beach activities.",
    category: "reglementation"
  },
  {
    id: "cot-permis-cotier",
    question_fr: "Jusqu'où peut-on naviguer avec le permis côtier ?",
    question_en: "How far can you sail with the coastal license?",
    answer_fr: "Jusqu'à 6 milles d'un abri",
    answer_en: "Up to 6 nautical miles from a shelter",
    context_fr: "Au-delà, il faut l'extension hauturière.",
    context_en: "Beyond that, the offshore extension is needed.",
    category: "reglementation"
  },
];
